import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiTrash2, FiChevronDown, FiChevronRight, FiClipboard, FiUserCheck, FiPaperclip } from 'react-icons/fi';
import {
  listarEtapasPadrao,
  criarEtapaPadrao,
  excluirEtapaPadrao,
  atualizarEtapa,
  atualizarProcessoPadrao,
  criarProcessoPadrao,
  excluirProcessoPadrao,
} from '../../../../services/etapas';
import styles from '../Onboard.module.css';

/* ===== Item de processo com toggle "Abrir Cadastro" ===== */

function ProcessoItem({ processo, onExcluir }) {
  const [abrirCadastro,  setAbrirCadastro]  = useState(processo.abrir_cadastro ?? false);
  const [requerAnexo,    setRequerAnexo]    = useState(processo.requer_anexo   ?? false);
  const [salvando,       setSalvando]       = useState(false);
  const [erro,           setErro]           = useState('');

  async function toggle(campo, valor, setter) {
    setSalvando(true);
    setErro('');
    try {
      await atualizarProcessoPadrao(processo.id, { [campo]: valor });
      setter(valor);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className={styles.processoPadraoItem}>
      <span className={styles.processoCircle} />
      <span className={styles.processoItemTitulo}>{processo.titulo}</span>

      {erro && <span className={styles.erroInline} style={{ fontSize: 11 }}>{erro}</span>}

      {/* Toggle Abrir Cadastro */}
      <button
        className={`${styles.btnToggleCadastro} ${abrirCadastro ? styles.btnToggleCadastroAtivo : ''}`}
        onClick={() => toggle('abrir_cadastro', !abrirCadastro, setAbrirCadastro)}
        disabled={salvando}
        title={abrirCadastro ? 'Cadastro ativo — clique para desativar' : 'Ativar botão de cadastro no card'}
        type="button"
      >
        <FiUserCheck size={12} />
        {abrirCadastro ? 'Cadastro' : 'Cadastro?'}
      </button>

      {/* Toggle Requer Anexo */}
      <button
        className={`${styles.btnToggleCadastro} ${requerAnexo ? styles.btnToggleAnexoAtivo : ''}`}
        onClick={() => toggle('requer_anexo', !requerAnexo, setRequerAnexo)}
        disabled={salvando}
        title={requerAnexo ? 'Anexo obrigatório — clique para desativar' : 'Exigir anexo antes de concluir'}
        type="button"
      >
        <FiPaperclip size={12} />
        {requerAnexo ? 'Anexo' : 'Anexo?'}
      </button>

      <button
        className={styles.btnIcone}
        onClick={() => onExcluir(processo.id)}
        aria-label="Excluir processo"
        type="button"
        disabled={salvando}
      >
        <FiTrash2 />
      </button>
    </div>
  );
}

/* ===== Item de etapa com processos expandíveis ===== */

function EtapaItem({ etapa, onExcluirEtapa }) {
  const [expandido,       setExpandido]       = useState(true);
  const [processos,       setProcessos]       = useState(etapa.processos || []);
  const [requerRegistro,  setRequerRegistro]  = useState(etapa.requer_registro ?? false);
  const [novoProc,        setNovoProc]        = useState('');
  const [salvando,        setSalvando]        = useState(false);
  const [salvandoToggle,  setSalvandoToggle]  = useState(false);
  const [erro,            setErro]            = useState('');

  async function handleAdicionarProcesso() {
    if (!novoProc.trim()) return;
    setSalvando(true);
    setErro('');
    try {
      const novo = await criarProcessoPadrao(etapa.id, novoProc.trim().toUpperCase());
      setProcessos(prev => [...prev, novo]);
      setNovoProc('');
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  async function handleExcluirProcesso(id) {
    try {
      await excluirProcessoPadrao(id);
      setProcessos(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      setErro(e.message);
    }
  }

  async function handleToggleRequerRegistro() {
    setSalvandoToggle(true);
    try {
      await atualizarEtapa(etapa.id, { requer_registro: !requerRegistro });
      setRequerRegistro(v => !v);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvandoToggle(false);
    }
  }

  return (
    <div className={styles.etapaPadraoCard}>
      <div className={styles.etapaPadraoCardHeader}>
        <button
          className={styles.btnExpandir}
          onClick={() => setExpandido(v => !v)}
          aria-label={expandido ? 'Recolher' : 'Expandir'}
        >
          {expandido ? <FiChevronDown /> : <FiChevronRight />}
        </button>
        <span className={styles.etapaTituloBold}>{etapa.titulo}</span>
        <span className={styles.etapaPadraoCount}>{processos.length} proc.</span>

        <button
          className={`${styles.btnRequerRegistro} ${requerRegistro ? styles.btnRequerRegistroAtivo : ''}`}
          onClick={handleToggleRequerRegistro}
          disabled={salvandoToggle}
          title={requerRegistro
            ? 'Requer registro ao concluir — clique para desativar'
            : 'Clique para exigir registro ao concluir cada item'}
        >
          <FiClipboard size={12} />
          {requerRegistro ? 'Requer Registro' : 'Requer Registro?'}
        </button>

        <button
          className={styles.btnIcone}
          onClick={() => onExcluirEtapa(etapa.id)}
          aria-label="Excluir etapa"
        >
          <FiTrash2 />
        </button>
      </div>

      {expandido && (
        <div className={styles.etapaPadraoBody}>
          {requerRegistro && (
            <div className={styles.requerRegistroBanner}>
              <FiClipboard size={13} />
              Ao concluir cada item desta etapa será solicitado o registro correspondente.
            </div>
          )}

          {erro && <p className={styles.erroInline}>{erro}</p>}

          {processos.length === 0 ? (
            <p className={styles.semProcessos}>Nenhum processo. Adicione abaixo.</p>
          ) : (
            processos.map(p => (
              <ProcessoItem
                key={p.id}
                processo={p}
                onExcluir={handleExcluirProcesso}
              />
            ))
          )}

          <div className={styles.processoAddRow}>
            <input
              className={styles.etapaInputSm}
              placeholder="Novo processo..."
              value={novoProc}
              onChange={e => setNovoProc(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdicionarProcesso()}
            />
            <button
              className={styles.btnPrimarioSm}
              onClick={handleAdicionarProcesso}
              disabled={salvando || !novoProc.trim()}
            >
              <FiPlus />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===== Modal principal ===== */

function EtapasPadraoModal({ onFechar }) {
  const [etapas,      setEtapas]      = useState([]);
  const [carregando,  setCarregando]  = useState(true);
  const [novoTitulo,  setNovoTitulo]  = useState('');
  const [salvando,    setSalvando]    = useState(false);
  const [erro,        setErro]        = useState('');

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onFechar(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  useEffect(() => {
    listarEtapasPadrao()
      .then(setEtapas)
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  async function handleCriarEtapa() {
    if (!novoTitulo.trim()) return;
    setSalvando(true);
    setErro('');
    try {
      const nova = await criarEtapaPadrao(novoTitulo.trim().toUpperCase());
      setEtapas(prev => [...prev, nova]);
      setNovoTitulo('');
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  async function handleExcluirEtapa(id) {
    try {
      await excluirEtapaPadrao(id);
      setEtapas(prev => prev.filter(e => e.id !== id));
    } catch (e) {
      setErro(e.message);
    }
  }

  return (
    <div
      className={`${styles.overlay} ${styles.overlayFullscreen}`}
    >
      <div className={styles.etapasPadraoModal}>
        <div className={styles.detalheHeader}>
          <div>
            <p className={styles.detalheEmpresa}>Etapas Padrão</p>
            <p className={styles.detalheNome}>Crie etapas com processos e templates de documentos</p>
          </div>
          <button className={styles.btnFechar} onClick={onFechar} aria-label="Fechar">
            <FiX />
          </button>
        </div>

        <div className={styles.detalheCorpo}>
          {erro && <div className={styles.erroFaixa}>{erro}</div>}

          <div className={styles.etapaAddRow}>
            <input
              className={styles.etapaInput}
              placeholder="Nome da nova etapa (ex: Boas-vindas, Abertura de Empresa)..."
              value={novoTitulo}
              onChange={e => setNovoTitulo(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCriarEtapa()}
            />
            <button
              className={styles.btnPrimario}
              onClick={handleCriarEtapa}
              disabled={salvando || !novoTitulo.trim()}
            >
              <FiPlus /> Criar
            </button>
          </div>

          {carregando ? (
            <p className={styles.vazio}>Carregando...</p>
          ) : etapas.length === 0 ? (
            <p className={styles.vazio}>Nenhuma etapa cadastrada ainda.</p>
          ) : (
            <div className={styles.etapasPadraoLista}>
              {etapas.map(etapa => (
                <EtapaItem
                  key={etapa.id}
                  etapa={etapa}
                  onExcluirEtapa={handleExcluirEtapa}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EtapasPadraoModal;
